import { Grid, useDeviceTypeContext, useStyles } from '@archipro-design/aria';
import DetailSection from '../detail-section/DetailSection';
import DetailModule from './DetailModule';
import * as S from './DetailModules.style';
import { FlexibleModuleEnum } from 'generated/graphql';
import { useLocation } from '@remix-run/react';
import type { PageBuilderModuleType } from '~/modules/page-builder/types';
import AdvertMediumRectangle from '~/modules/adverts/component/advert-medium-rectangle/AdvertMediumRectangle';
import AdvertBillboard from '~/modules/adverts/component/advert-billboard/AdvertBillboard';
import type { ModulesWithAds } from '~/modules/adverts/type';
import { getScrollParams } from '~/modules/page-builder/util/get-scroll-params';
import { useEffect, useRef } from 'react';

interface DetailModuleProps {
    modules: ModulesWithAds;
    articleGatingEnabled?: boolean;
}

const DetailModules = ({
    modules,
    articleGatingEnabled,
}: DetailModuleProps) => {
    const { css } = useStyles({ articleGatingEnabled });
    const { mobile: isMobile } = useDeviceTypeContext();

    const location = useLocation();
    const urlSearchParams = new URLSearchParams(location.search);
    const urlVideoParam = urlSearchParams.get('video');
    let urlVideoIndex = urlVideoParam ? Number(urlVideoParam) : -1;
    let autoPlayVideoModuleId = '';
    // top banner is index 1
    if (urlVideoIndex > 1) {
        const allVideos = modules.filter(
            (m) => m.presetType === FlexibleModuleEnum.SingleVideoModule
        );
        const videoCount = allVideos.length;
        if (videoCount > 0) {
            if (urlVideoIndex > videoCount || urlVideoIndex < 1) {
                // default to first video is url has video=9000
                urlVideoIndex = 1;
            }
            autoPlayVideoModuleId = allVideos[urlVideoIndex - 1]!.id;
        }
    }

    // if article gating is on, then render the first two non ads modules
    const renderModules = articleGatingEnabled
        ? modules
              .filter(
                  (module) => module.presetType !== 'ads' || !('ad' in module)
              )
              .splice(0, 2)
        : modules;

    const hasScrolledToTagID = useRef(false);
    useEffect(() => {
        const { scrollToTagID } = getScrollParams(window?.location.search);
        if (scrollToTagID && !hasScrolledToTagID.current) {
            const moduleShouldScroll = modules.find(
                (m) =>
                    'columns' in m &&
                    m.columns.some((c) =>
                        c.uniqueApprovedTags?.some(
                            (t) => t.id === scrollToTagID
                        )
                    )
            );
            if (moduleShouldScroll) {
                const element = document.getElementById(moduleShouldScroll.id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                    hasScrolledToTagID.current = true;
                }
            }
        }
    }, [modules]);

    return (
        <Grid columns={1} className={css(S.SectionContainer)}>
            {renderModules.map((module) => {
                const isAd = module.presetType === 'ads' && 'ad' in module;
                const percentContentViewed =
                    'memberSortOrder' in module
                        ? (module.memberSortOrder + 1) / modules.length
                        : 0;
                return (
                    <DetailSection
                        key={module.id}
                        isAd={isAd}
                        percentContentViewed={percentContentViewed}
                        moduleId={module.id}
                    >
                        {isAd ? (
                            isMobile ? (
                                <AdvertMediumRectangle {...module.ad} />
                            ) : (
                                <AdvertBillboard {...module.ad} />
                            )
                        ) : (
                            <DetailModule
                                module={module as PageBuilderModuleType}
                                autoPlayVideoModuleId={autoPlayVideoModuleId}
                            />
                        )}
                    </DetailSection>
                );
            })}
            {articleGatingEnabled && <div className={css(S.BlurMask)} />}
        </Grid>
    );
};

export default DetailModules;
